import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchDuplicateReport, dismissDuplicateGroup } from '../api';
import MergeCustomersModal from './MergeCustomersModal';
import './duplicatecustomerreport.css';

const REASON_LABELS = { email: 'Same email', phone: 'Same phone', name: 'Same name' };
const FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'email', label: 'Same email' },
  { value: 'phone', label: 'Same phone' },
  { value: 'name', label: 'Same name' },
];

const pluralize = (count, word) => `${count} ${word}${count === 1 ? '' : 's'}`;

/**
 * Admin Settings → Duplicate Customers. Lists customers that share a phone, email or
 * name. "Merge…" combines a group into one customer; "Not a duplicate" hides a group
 * until another customer joins it. Clicking a customer opens their page in Accounts.
 */
const DuplicateCustomerReport = ({ onOpenCustomer }) => {
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [dismissing, setDismissing] = useState(null);
  const [mergeIds, setMergeIds] = useState(null);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchDuplicateReport();
      setGroups(data.groups || []);
    } catch (err) {
      console.error('Error loading duplicate report:', err);
      setError('Failed to load the duplicate report.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleDismiss = async (group) => {
    setDismissing(group.key);
    try {
      await dismissDuplicateGroup(group);
      setGroups((prev) => prev.filter((g) => !(g.key === group.key && g.customer_ids === group.customer_ids)));
    } catch (err) {
      console.error('Error dismissing duplicate group:', err);
      setError('Failed to mark the group as not a duplicate.');
    } finally {
      setDismissing(null);
    }
  };

  const openCustomer = (customerId, mergedCount) => {
    if (onOpenCustomer) onOpenCustomer(); // close the settings overlay
    navigate(`/accounts?customer=${customerId}${mergedCount ? `&merged=${mergedCount}` : ''}`);
  };

  const handleMerged = (customer, mergedCount) => {
    setMergeIds(null);
    openCustomer(customer.id, mergedCount);
  };

  const visible = filter === 'all'
    ? groups
    : groups.filter((g) => g.reasons.some((r) => r.type === filter));

  return (
    <div className="dupreport">
      <div className="dupreport-toolbar">
        <p className="dupreport-intro">
          Customers who share an email, phone number or name. Merge a group into one
          customer, or mark it &ldquo;Not a duplicate&rdquo; to hide it.
        </p>
        <div className="dupreport-controls">
          <select value={filter} onChange={(e) => setFilter(e.target.value)} aria-label="Filter duplicate groups">
            {FILTERS.map((f) => (
              <option key={f.value} value={f.value}>{f.label}</option>
            ))}
          </select>
          <button type="button" onClick={load} disabled={loading}>Refresh</button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {loading ? (
        <div className="dupreport-empty">Loading…</div>
      ) : visible.length === 0 ? (
        <div className="dupreport-empty">No possible duplicates to review.</div>
      ) : (
        <>
          <div className="dupreport-count">{pluralize(visible.length, 'group')} to review</div>
          <ul className="dupreport-groups">
            {visible.map((group) => (
              <li key={`${group.key}#${group.customer_ids}`} className="dupreport-group">
                <div className="dupreport-group-header">
                  <div className="dupreport-reasons">
                    {group.reasons.map((reason) => (
                      <span key={reason.type} className="dupreport-reason">
                        {REASON_LABELS[reason.type]}
                        <span className="dupreport-reason-value">{reason.value}</span>
                      </span>
                    ))}
                  </div>
                  <div className="dupreport-actions">
                    <button
                      type="button"
                      className="dupreport-merge"
                      onClick={() => setMergeIds(group.customers.map((c) => c.id))}
                    >
                      Merge…
                    </button>
                    <button
                      type="button"
                      className="dupreport-dismiss"
                      onClick={() => handleDismiss(group)}
                      disabled={dismissing === group.key}
                    >
                      Not a duplicate
                    </button>
                  </div>
                </div>
                <table className="dupreport-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Phone</th>
                      <th>Email</th>
                      <th>History</th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.customers.map((customer) => (
                      <tr key={customer.id}>
                        <td>
                          <button type="button" className="dupreport-link" onClick={() => openCustomer(customer.id)}>
                            {customer.name}
                          </button>
                        </td>
                        <td>{customer.phone}</td>
                        <td className="dupreport-email">{customer.email}</td>
                        <td className="dupreport-history">
                          {pluralize(customer.work_order_count, 'work order')}, {pluralize(customer.quote_count, 'quote')}, {pluralize(customer.job_count, 'job')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </li>
            ))}
          </ul>
        </>
      )}

      {mergeIds && (
        <MergeCustomersModal
          customerIds={mergeIds}
          onClose={() => setMergeIds(null)}
          onMerged={handleMerged}
        />
      )}
    </div>
  );
};

export default DuplicateCustomerReport;
